import { makeid } from 'utils/helpers/makeid';
import { DateFormatter } from 'infrastructure/functions';

import ScriptWrapper from './script-wrapper';
import { addField, formatList, hasField, mapValuesToText } from '../helper';

import type { BlurbContext, ITemplateProps } from '../types';
import type {
  ExtractStepValues,
  TFormTemplateItem,
} from '../../../molecules/base-form-template/types';

export const createProgramIntroTemplate = (data: ITemplateProps) => {
  const { userFullname } = data;
  return [
    {
      key: makeid(10),
      name: 'greetingComplianceHipaaVerification',
      type: 'checkbox',
      title: <p>Greeting & Compliance / HIPAA Verification</p>,
      description: (
        <ScriptWrapper>
          "Hello, this is {userFullname} with Tellihealth. I'm calling to
          welcome you to the Remote Patient Monitoring program."
          <br />
          "For quality and compliance purposes, this call may be recorded."
          <br />
          "To protect your privacy, can you please confirm your full name and
          date of birth?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Patient identity verified (name + DOB)',
          value: 'patientIdentityVerified',
        },
        {
          key: makeid(10),
          label: 'Caregiver present → Consent confirmed',
          value: 'caregiverPresentConsentConfirmed',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Are you the patient's caregiver? Do I have the patient's
                  consent to speak with you today?"
                </ScriptWrapper>
              ),
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Recorded line notification provided',
          value: 'recordedLineNotificationProvided',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'readingAdherenceBarrierCheck',
      type: 'radio',
      title: <p>Reading Adherence & Barrier Check</p>,
      description: (
        <ScriptWrapper>
          "Before we get started, I want to check in to see if you’ve already
          been able to take any readings, or if anything might get in the way of
          doing so."
          <br />
          "If something does come up — like device questions, supplies, or
          schedule challenges — please know we’re here to help."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Consistent readings',
          value: 'consistentReadings',
        },
        {
          key: makeid(10),
          label: 'Inconsistent readings',
          value: 'inconsistentReadings',
        },
        {
          key: makeid(10),
          label: 'No readings',
          value: 'noReadings',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Thank you for letting me know — I’ll document that so we can
                  address it."
                </ScriptWrapper>
              ),
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Other',
          value: 'other',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'textarea',
              name: 'readingAdherenceCheckOther',
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Barriers identified',
          value: 'barriersIdentified',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Thank you for letting me know — I’ll document that so we can
                  support you."
                </ScriptWrapper>
              ),
            },
            {
              key: makeid(10),
              name: 'barriersList',
              type: 'checkbox',
              title: <p>Barriers</p>,
              description: '',
              required: false,
              closable: true,
              options: [
                {
                  key: makeid(10),
                  label: 'Device',
                  value: 'device',
                },
                {
                  key: makeid(10),
                  label: 'Supplies',
                  value: 'supplies',
                },
                {
                  key: makeid(10),
                  label: 'Schedule',
                  value: 'schedule',
                },
                {
                  key: makeid(10),
                  label: 'Education',
                  value: 'education',
                },
                {
                  key: makeid(10),
                  label: 'Connectivity',
                  value: 'connectivity',
                },
                {
                  key: makeid(10),
                  label: 'Power/charging',
                  value: 'powerCharging',
                },
                {
                  key: makeid(10),
                  label: 'Other',
                  value: 'other',
                  hideSubitemsIfParentNotSelected: true,
                  subItems: [
                    {
                      key: makeid(10),
                      type: 'textarea',
                      name: 'barriersListOther',
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
    {
      key: makeid(10),
      name: 'programOverviewProvided',
      type: 'checkbox',
      title: <p>Program Overview Provided</p>,
      description: (
        <ScriptWrapper>
          "You've been enrolled in Remote Patient Monitoring, or RPM, through
          your clinic and Tellihealth."
          <br />
          "This program allows you to take health readings at home, which are
          securely shared with your care team."
          <br />
          "It helps your clinic track trends over time and address concerns
          earlier, between office visits."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'RPM program explained',
          value: 'rpmProgramExplained',
        },
        {
          key: makeid(10),
          label: 'Purpose and benefits reviewed',
          value: 'purposeAndBenefitsReviewed',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'participationExpectationsReviewed',
      type: 'checkbox',
      title: <p>Participation Expectations Reviewed</p>,
      description: (
        <ScriptWrapper>
          "You'll be asked to take readings as directed by your clinic, usually
          on a daily basis."
          <br />
          "Our team may reach out if we notice missing readings, trends, or if
          you contact us with questions."
          <br />
          "This program is designed to support you — not to replace your regular
          care."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Reading expectations reviewed',
          value: 'readingExpectationsReviewed',
        },
        {
          key: makeid(10),
          label: 'Support role of RPM team explained',
          value: 'supportRoleOfRpmTeamExplained',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'insuranceVerificationOnFile',
      type: 'radio',
      title: <p>Insurance Verification (On File Only)</p>,
      description: (
        <ScriptWrapper>
          "I want to confirm the type of insurance we have on file for you."
          <br />
          "What type of insurance do you have listed with your clinic: Medicare,
          Medicare Advantage, or Commercial?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Medicare',
          value: 'medicare',
        },
        {
          key: makeid(10),
          label: 'Medicare Advantage (plan selected)',
          value: 'medicareAdvantage',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Which plan is listed?"
                  <br />
                  "Thank you — I'll confirm what's documented in your chart."
                </ScriptWrapper>
              ),
            },
            {
              key: makeid(10),
              type: 'checkbox',
              name: 'insuranceTypeOnFile',
              options: [
                {
                  key: makeid(10),
                  label: 'Insurance type on file confirmed',
                  value: 'insuranceTypeOnFileConfirmed',
                },
              ],
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Commercial',
          value: 'commercial',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'cellPhoneVerification',
      type: 'radio',
      title: <p>Cell Phone Verification</p>,
      description: (
        <ScriptWrapper>
          "And what's the best cell phone number to reach you? Is [number on
          file] still correct?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Cell phone confirmed',
          value: 'cellPhoneConfirmed',
        },
        {
          key: makeid(10),
          label: 'Updated',
          value: 'updated',
        },
        {
          key: makeid(10),
          label: 'No cell phone',
          value: 'noCellPhone',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'textingEnrollment',
      type: 'radio',
      title: <p>Texting Enrollment</p>,
      description: (
        <ScriptWrapper>
          If patient has a cell phone: "We also offer text messages for
          reminders and updates related to your RPM care. Would you like to
          receive text messages from us?"
          <br />
          If yes: enroll. If no: document decline. If no cell phone: do not
          offer texting.
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Already enrolled',
          value: 'alreadyEnrolled',
        },
        {
          key: makeid(10),
          label: 'Offered/Accepted',
          value: 'offeredAccepted',
        },
        {
          key: makeid(10),
          label: 'Offered/Declined',
          value: 'offeredDeclined',
        },
        {
          key: makeid(10),
          label: 'Not eligible',
          value: 'notEligible',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'monthlyInteractionRequirement',
      type: 'checkbox',
      title: <p>Monthly Interaction Requirement + Scheduling</p>,
      description: (
        <ScriptWrapper>
          “As a reminder, these monthly phone calls are required to remain
          active in the RPM program.”
          <br />
          “They help us stay connected and identify issues early.”
          <br />
          “Would you like to schedule your next monthly check-in now, or should
          we call you closer to the date?”
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Monthly phone call requirement reviewed',
          value: 'monthlyPhoneCallRequirementReviewed',
        },
        {
          key: makeid(10),
          label: 'Program monitoring process explained',
          value: 'programMonitoringProcessExplained',
        },
        {
          key: makeid(10),
          label: 'Nurse or clinic outreach for out-of-range readings explained',
          value: 'nurseOrClinicOutreachForOutOfRangeReadingsExplained',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'monthlyInteractionScheduling',
      title: <p>Monthly Interaction Scheduling</p>,
      type: 'radio',
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'First monthly call scheduled (date)',
          value: 'nextMonthlyCheckInScheduled',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'input-date',
              required: false,
              name: 'nextMonthlyCheckInScheduledDate',
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Patient declined scheduling',
          value: 'patientDeclinedScheduling',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'closingAdditionalQuestions',
      type: 'radio',
      title: <p>Closing - Additional Questions</p>,
      description: (
        <ScriptWrapper>
          "Before we disconnect, do you have any additional questions for me
          today?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Had no additional questions',
          value: 'noAdditionalQuestions',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: <ScriptWrapper>"Great — thank you."</ScriptWrapper>,
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Asked questions — addressed during call',
          value: 'hasAdditionalQuestions',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>"Does that answer your question?"</ScriptWrapper>
              ),
            },
          ],
        },
      ],
    },
    {
      key: makeid(10),
      name: 'closingUnderstanding',
      type: 'radio',
      title: <p>Closing - Understanding</p>,
      description: (
        <ScriptWrapper>
          "Just to confirm, do you feel you understand everything we discussed
          today?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Verbalized full understanding',
          value: 'full',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: <ScriptWrapper>"Perfect."</ScriptWrapper>,
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Verbalized partial understanding',
          value: 'partial',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Thank you for letting me know. Is there anything I can
                  clarify before we finish?"
                </ScriptWrapper>
              ),
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Unable to verbalize understanding',
          value: 'unable',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Thank you — I'll document that and we can follow up as
                  needed."
                </ScriptWrapper>
              ),
            },
          ],
        },
      ],
    },
    {
      key: makeid(10),
      name: '_empty',
      type: 'empty',
      title: <p>Final close</p>,
      description: (
        <ScriptWrapper>
          "Thank you for your time today. Please feel free to reach out if you
          need additional support. Take care."
        </ScriptWrapper>
      ),
    },
  ] as const satisfies TFormTemplateItem[];
};

export type TProgramIntroTemplateValues = ExtractStepValues<
  ReturnType<typeof createProgramIntroTemplate>
>;

export const createProgramIntroBlurb = (
  data: TProgramIntroTemplateValues,
  ctx: BlurbContext,
) => {
  const {
    greetingComplianceHipaaVerification,
    readingAdherenceBarrierCheck,
    readingAdherenceCheckOther,
    barriersList,
    barriersListOther,
    programOverviewProvided,
    participationExpectationsReviewed,
    insuranceVerificationOnFile,
    cellPhoneVerification,
    textingEnrollment,
    monthlyInteractionRequirement,
    monthlyInteractionScheduling,
    nextMonthlyCheckInScheduledDate,
    closingAdditionalQuestions,
    closingUnderstanding,
  } = data;
  const txt: string[] = [];

  if (
    greetingComplianceHipaaVerification?.length &&
    !hasField(ctx, 'greetingComplianceHipaaVerification')
  ) {
    addField(ctx, 'greetingComplianceHipaaVerification');
    const map = {
      patientIdentityVerified:
        'Greeting and compliance completed. Patient identity verified via name and date of birth.',
      recordedLineNotificationProvided:
        'Patient notified call may be recorded for quality and compliance purposes.',
      caregiverPresentConsentConfirmed:
        'Caregiver participation confirmed and patient consent obtained.',
    };
    txt.push(...mapValuesToText(greetingComplianceHipaaVerification, map));
  }

  if (
    readingAdherenceBarrierCheck &&
    !hasField(ctx, 'readingAdherenceBarrierCheck')
  ) {
    addField(ctx, 'readingAdherenceBarrierCheck');
    const map = {
      consistentReadings: 'consistent readings',
      inconsistentReadings: 'inconsistent readings',
      missedReadings: 'missed readings',
      noReadings: 'no readings',
      barriersIdentified: 'barriers identified',
      other: readingAdherenceCheckOther,
    };

    const value = map[readingAdherenceBarrierCheck];

    if (value) {
      txt.push(`Reading adherence reviewed. Patient reports ${value}.`);
    }

    if (
      readingAdherenceBarrierCheck === 'barriersIdentified' &&
      barriersList?.length &&
      !hasField(ctx, 'barriersList')
    ) {
      addField(ctx, 'barriersList');
      const barriersMap = {
        device: 'device issues',
        supplies: 'supply availability',
        schedule: 'schedule conflicts',
        education: 'education needs',
        connectivity: 'connectivity issues',
        powerCharging: 'power or charging issues',
        other: barriersListOther,
      };
      const items = mapValuesToText(barriersList, barriersMap);
      if (items.length) {
        txt.push(
          `Barriers identified and documented, including ${formatList(items)}.`,
        );
      }
    }
  }

  if (
    programOverviewProvided?.length &&
    !hasField(ctx, 'programOverviewProvided')
  ) {
    addField(ctx, 'programOverviewProvided');
    const map = {
      rpmProgramExplained: 'RPM program explained',
      purposeAndBenefitsReviewed: 'purpose and benefits reviewed',
    };
    const items = mapValuesToText(programOverviewProvided, map);
    if (items.length) {
      txt.push(
        `RPM program overview provided, including ${formatList(items)}.`,
      );
    }
  }

  if (
    participationExpectationsReviewed?.length &&
    !hasField(ctx, 'participationExpectationsReviewed')
  ) {
    addField(ctx, 'participationExpectationsReviewed');
    const map = {
      readingExpectationsReviewed: 'reading expectations reviewed',
      supportRoleOfRpmTeamExplained: 'support role of RPM team explained',
    };
    const items = mapValuesToText(participationExpectationsReviewed, map);
    if (items.length) {
      txt.push(
        `Program participation expectations reviewed with patient, including ${formatList(
          items,
        )}.`,
      );
    }
  }

  if (
    insuranceVerificationOnFile &&
    !hasField(ctx, 'insuranceVerificationOnFile')
  ) {
    addField(ctx, 'insuranceVerificationOnFile');
    const map = {
      medicare: 'Medicare',
      medicareAdvantage: 'Medicare Advantage (plan selected)',
      commercial: 'Commercial',
    };
    txt.push(
      `Insurance type confirmed as ${map[insuranceVerificationOnFile]} based on information on file and updated in patient chart as needed.`,
    );
  }

  if (cellPhoneVerification && !hasField(ctx, 'cellPhoneVerification')) {
    addField(ctx, 'cellPhoneVerification');
    const map = {
      cellPhoneConfirmed: 'confirmed',
      updated: 'updated',
      noCellPhone: 'no cell phone',
    };
    txt.push(
      `Cell phone contact reviewed. Number ${map[cellPhoneVerification]}. `,
    );
  }

  if (textingEnrollment && !hasField(ctx, 'textingEnrollment')) {
    addField(ctx, 'textingEnrollment');
    const map = {
      alreadyEnrolled: 'already enrolled',
      offeredAccepted: 'offered and accepted',
      offeredDeclined: 'offered and declined',
      notEligible: 'not eligible—no cell phone',
    };
    txt.push(`Texting enrollment reviewed ${map[textingEnrollment]}.`);
  }

  if (
    monthlyInteractionRequirement?.length &&
    !hasField(ctx, 'monthlyInteractionRequirement')
  ) {
    addField(ctx, 'monthlyInteractionRequirement');
    const map = {
      monthlyPhoneCallRequirementReviewed: 'monthly phone call requirement',
      programMonitoringProcessExplained: 'program monitoring process explained',
      nurseOrClinicOutreachForOutOfRangeReadingsExplained:
        'that out-of-range readings may result in nurse or clinic outreach',
    };
    const items = mapValuesToText(monthlyInteractionRequirement, map);
    if (items.length) {
      txt.push(
        `Monthly interaction requirement reviewed with patient, including ${formatList(
          items,
        )}.`,
      );
    }
  }

  if (
    monthlyInteractionScheduling &&
    !hasField(ctx, 'monthlyInteractionScheduling')
  ) {
    addField(ctx, 'monthlyInteractionScheduling');
    const map = {
      nextMonthlyCheckInScheduled: 'scheduled',
      patientDeclinedScheduling: 'not scheduled—patient declined',
    };
    let val = map[monthlyInteractionScheduling];
    if (
      monthlyInteractionScheduling === 'nextMonthlyCheckInScheduled' &&
      nextMonthlyCheckInScheduledDate
    ) {
      val += `in ${DateFormatter({
        date: nextMonthlyCheckInScheduledDate,
      })}`;
    }

    txt.push(`Next check-in ${val}.`);
  }

  if (
    closingAdditionalQuestions &&
    !hasField(ctx, 'closingAdditionalQuestions')
  ) {
    addField(ctx, 'closingAdditionalQuestions');

    const map = {
      noAdditionalQuestions: 'had no additional questions',
      hasAdditionalQuestions:
        'asked questions which were addressed during the call',
    };

    txt.push(
      `Call concluded after confirming patient ${map[closingAdditionalQuestions]}.`,
    );
  }

  if (closingUnderstanding && !hasField(ctx, 'closingUnderstanding')) {
    addField(ctx, 'closingUnderstanding');

    const map = {
      full: 'verbalized full understanding',
      partial: 'verbalized partial understanding',
      unable: 'was unable to verbalize understanding',
    };

    txt.push(
      `Patient ${map[closingUnderstanding]} of the information discussed.`,
    );
  }

  return txt.join(' ');
};
